import React, { useState, useEffect, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import { Icon } from '@iconify/react';
import api from '../../services/api';
import { Modal } from 'react-bootstrap';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import { toast } from 'react-toastify';

const BrokersTable = () => {
    const [brokers, setBrokers] = useState([]);
    const [selectedBroker, setSelectedBroker] = useState(null);
    const [actionType, setActionType] = useState('');
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // --- Utility ---
    const formatNumber = (number) => {
        const num = Number(number);
        if (isNaN(num)) {
            return 'N/A';
        }
        if (num >= 1000) {
            return new Intl.NumberFormat().format(num);
        }
        return num.toString();
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // --- Data Fetching ---
    const fetchBrokers = useCallback(async (page) => {
        setLoading(true);
        try {
            const response = await api.get(`/brokersList?page=${page}&per_page=${perPage}`);
            if (response.data && response.data.response) {
                setBrokers(response.data.response.data || []);
                setTotalRows(response.data.response.total || 0);
            } else {
                console.error("API response format is not as expected:", response.data);
                setBrokers([]);
                setTotalRows(0);
                toast.error("Failed to fetch brokers: Invalid format received.");
            }
        } catch (error) {
            console.error("Error fetching brokers:", error);
            toast.error("Failed to fetch brokers data.");
            setBrokers([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, [perPage]);

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchBrokers(page);
    };

    // Handle per page change
    const handlePerRowsChange = async (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
        fetchBrokers(page);
    };

    // Fetch data on initial mount and when perPage changes
    useEffect(() => {
        fetchBrokers(currentPage);
    }, [currentPage, fetchBrokers]);

    // --- Action Handlers ---
    const confirmAction = (broker, type) => {
        if (!broker || !broker.id) {
            console.error("Confirm action called with invalid broker data:", broker);
            return;
        }
        setSelectedBroker(broker);
        setActionType(type);
        setShowConfirmationModal(true);
    };

    const handleCloseModal = () => {
        setShowConfirmationModal(false);
        setTimeout(() => {
            setSelectedBroker(null);
            setActionType('');
        }, 200);
    };

    const handleConfirmAction = async () => {
        if (!selectedBroker || !selectedBroker.id || !actionType) {
            console.error("Confirm action execution called without proper selection or action type.");
            handleCloseModal();
            return;
        }
        try {
            const endpoint = actionType === 'block' ? 'block' : 'unblock';
            await api.put(`/users/${selectedBroker.id}/${endpoint}`);
            toast.success(`Broker ${actionType === 'block' ? 'blocked' : 'unblocked'} successfully!`);
            fetchBrokers(currentPage);
            handleCloseModal();
        } catch (error) {
            toast.error(`Failed to ${actionType} broker.`);
            console.error(`${actionType} broker error:`, error.response?.data || error.message);
            handleCloseModal();
        }
    };

    // DataTable columns configuration
    const columns = [
        {
            name: 'Profile',
            cell: row => (
                <div className="d-flex align-items-center">
                    {row.picture ? (
                        <img
                            src={row.picture}
                            alt={`${row.name}'s profile`}
                            className="rounded-circle"
                            style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'assets/images/avatar.png';
                            }}
                        />
                    ) : (
                        <img
                            src="assets/images/avatar.png"
                            alt="Default profile"
                            className="rounded-circle"
                            style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                        />
                    )}
                </div>
            ),
            width: '80px',
            sortable: false,
        },
        {
            name: 'Name',
            selector: row => row.name || 'N/A',
            sortable: true,
        },
        {
            name: 'Email',
            selector: row => row.email || 'N/A',
            sortable: true,
        },
        {
            name: 'Mobile',
            selector: row => row.mobile || 'N/A',
            sortable: true,
        },
        {
            name: 'Created At',
            selector: row => row.created_at,
            cell: row => formatDate(row.created_at),
            sortable: true,
        },
        // {
        //     name: 'National ID',
        //     cell: row => (
        //         row.nationalID ? (
        //             <a href={row.nationalID} target="_blank" rel="noopener noreferrer" title="View National ID">
        //                 <img
        //                     src={row.nationalID}
        //                     alt="National ID Preview"
        //                     style={{ maxWidth: '100px', maxHeight: '50px', cursor: 'pointer', objectFit: 'contain', border: '1px solid #ccc' }}
        //                     onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; e.target.alt = 'Image unavailable'; }}
        //                 />
        //             </a>
        //         ) : 'No ID'
        //     ),
        //     sortable: false,
        // },
        {
            name: 'Total CILs',
            selector: row => Number(row.cil_count) || 0,
            sortable: true,
        },
        {
            name: 'Total EOIs',
            selector: row => Number(row.eoi_count) || 0,
            sortable: true,
        },
        {
            name: 'Total Sales',
            selector: row => Number(row.total_sales) || 0,
            sortable: true,
        },
        {
            name: 'Commissions Received',
            selector: row => formatNumber(row.commission_recieved),
            sortable: true,
        },
        {
            name: 'Actions',
            cell: row => (
                <button
                    onClick={() => confirmAction(row, row.isBlocked === 0 ? 'block' : 'unblock')}
                    className={`w-32-px h-32-px rounded-circle d-inline-flex align-items-center justify-content-center border-0 p-0 ${
                        row.isBlocked === 0
                            ? "bg-danger-focus text-danger-main"
                            : "bg-success-focus text-success-main"
                    }`}
                    title={row.isBlocked === 0 ? 'Block Broker' : 'Unblock Broker'}
                    aria-label={row.isBlocked === 0 ? `Block ${row.name}` : `Unblock ${row.name}`}
                >
                    <Icon icon={row.isBlocked === 0 ? "mdi:account-cancel-outline" : "mdi:account-lock-open-outline"} width="20" height="20" />
                </button>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    // CSV export headers
    const csvHeaders = [
        { label: 'Name', key: 'name' },
        { label: 'Email', key: 'email' },
        { label: 'Mobile', key: 'mobile' },
        { label: 'Total CILs', key: 'cil_count' },
        { label: 'Total EOIs', key: 'eoi_count' },
        { label: 'Total Sales', key: 'total_sales' },
        { label: 'Commissions Received', key: 'commission_recieved' },
    ];

    // Custom styles for DataTable
    const customStyles = {
        rows: {
            style: {
                fontSize: '15px',
            },
        },
        cells: {
            style: {
                padding: '12px',
            },
        },
    };

    return (
        <div className="card basic-data-table px-3 py-3">
            <div className="card-body">
                {/* Search and Export Controls */}
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <input
                        type="text"
                        placeholder="Search brokers..."
                        value={filterText}
                        onChange={e => setFilterText(e.target.value)}
                        className="form-control"
                        style={{ maxWidth: '300px' }}
                    />

                    <CSVLink
                        data={brokers}
                        headers={csvHeaders}
                        filename="brokers-data.csv"
                        className="btn btn-secondary"
                    >
                        Export CSV
                    </CSVLink>
                </div>

                {/* DataTable Component */}
                <DataTable
                    columns={columns}
                    data={brokers}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    highlightOnHover
                    responsive
                    striped
                    progressPending={loading}
                    customStyles={customStyles}
                    noDataComponent="No brokers found"
                />

                {/* Confirmation Modal */}
                {showConfirmationModal && selectedBroker && (
                    <DeleteConfirmationModal
                        show={showConfirmationModal}
                        handleDelete={handleConfirmAction}
                        handleClose={handleCloseModal}
                        content={
                            <>
                                <Modal.Header closeButton>
                                    <Modal.Title>
                                        {actionType === 'block' ? 'Block Broker' : 'Unblock Broker'}
                                    </Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    <p>Are you sure you want to {actionType} the broker "{selectedBroker?.name ?? 'this broker'}"?</p>
                                </Modal.Body>
                            </>
                        }
                    />
                )}
            </div>
        </div>
    );
};

export default BrokersTable;